import { FC, memo, useCallback, useEffect, useRef, useState } from "react";

import { useLongPressSwipe } from "@/hooks";

import {
    StyledCenterLine,
    StyledPresetBtn,
    StyledPresetsRow,
    StyledRoot,
    StyledRulerWrap,
    StyledSliderZone,
    StyledTick,
    StyledTicksRow,
    StyledZoomBadge,
} from "./styled";

// Кандидати для пресетів зуму. З них далі будемо вибирати підходящі в залежності від min/max камери.
const ALL_PRESET_CANDIDATES = [0.5, 1, 2, 3, 5, 10, 20, 30];

const TICKS_PER_UNIT = 10; // кількість тiкiв на 1× зуму
const TICK_SPACING = 5.5; // відстань між тіками (ширина + gap)

// Формує список пресетів в залежності від можливостей камери. Ідея — не показувати зайві значення, якщо зум маленький,і не перевантажувати UI, якщо зум великий.
function derivePresets(min: number, max: number): number[] {
    const candidates = ALL_PRESET_CANDIDATES.filter(
        (p) => p >= min && p <= max,
    );

    if (max <= 3) return candidates;
    if (max <= 6) return candidates.filter((p) => p >= 1);
    if (max <= 15) return candidates.filter((p) => [1, 2, 3, 5].includes(p));

    const base = candidates.filter((p) => [1, 2, 3, 10].includes(p));

    // якщо max щось типу 17 — додамо 20 (але тільки якщо воно реально ≤ max)
    const roundedMax = Math.round(max / 10) * 10;
    if (roundedMax > 10 && !base.includes(roundedMax) && roundedMax <= max) {
        base.push(roundedMax);
    }

    return base;
}

// Форматування числа в "1×", "1.5×" і т.д.
const fmtZoom = (v: number) =>
    v === Math.round(v) ? `${v}×` : `${v.toFixed(1)}×`;

interface IZoomControlsProps {
    stream: MediaStream | null;
    isReady: boolean;
}

interface ZoomCapabilities {
    min: number;
    max: number;
    step: number;
}

export const ZoomControls: FC<IZoomControlsProps> = memo(
    ({ stream, isReady }) => {
        // Можливості зуму з камери (min/max/step)
        const [capabilities, setCapabilities] =
            useState<ZoomCapabilities | null>(null);

        // Чи показуємо зараз повзунок-лінійку
        const [sliderMode, setSliderMode] = useState(false);

        // Таймер для автоприховування лінійки
        const hideTimer = useRef<number | null>(null);

        // Зчитуємо capabilities камери, коли зʼявляється stream
        useEffect(() => {
            if (!stream || !isReady) return;

            const track = stream.getVideoTracks()[0];
            if (!track) return;

            const caps = track.getCapabilities() as MediaTrackCapabilities & {
                zoom?: { min: number; max: number; step: number };
            };

            if (caps.zoom) {
                setCapabilities({
                    min: caps.zoom.min,
                    max: caps.zoom.max,
                    step: caps.zoom.step ?? 0.1,
                });
            } else {
                // Якщо зум не підтримується — просто ховаємо контрол
                setCapabilities(null);
            }
        }, [stream, isReady]);

        // Фізично застосовуємо зум до камери
        const applyZoom = useCallback(
            (z: number) => {
                if (!stream) return;
                const track = stream.getVideoTracks()[0];

                track
                    ?.applyConstraints({
                        advanced: [{ zoom: z } as MediaTrackConstraints],
                    })
                    .catch(console.warn);
            },
            [stream],
        );

        // Показуємо лінійку і запускаємо таймер автоприховування
        const showSlider = useCallback(() => {
            setSliderMode(true);

            if (hideTimer.current) clearTimeout(hideTimer.current);

            hideTimer.current = window.setTimeout(
                () => setSliderMode(false),
                2000,
            );
        }, []);

        const min = capabilities?.min ?? 1;
        const max = capabilities?.max ?? 10;
        const step = capabilities?.step ?? 0.1;

        /**
         * Хук для long-press + swipe
         * Дає нам:
         *  - zoom (поточне значення)
         *  - bind'и для жестів
         */
        const {
            value: zoom,
            triggerBind,
            moveBind,
            setValue: setZoom,
        } = useLongPressSwipe({
            initial: Math.max(1, min),
            min,
            max,
            step,
            longPressMs: 280,
            sensitivity: 180,

            onChange: (v) => {
                applyZoom(v);
                showSlider();
            },

            onLongPressStart: showSlider,
        });

        // Клік по пресету (кнопці 1×, 2× і т.д.)
        const handlePreset = useCallback(
            (z: number) => {
                const clamped = Math.min(max, Math.max(min, z));
                setZoom(clamped);
                applyZoom(clamped);
                setSliderMode(false);
            },
            [max, min, setZoom, applyZoom],
        );

        // якщо камера не підтримує zoom — нічого не рендеримо
        if (!capabilities) return null;

        const presets = derivePresets(min, max);

        // ЛОГІКА ЛІНІЙКИ

        // загальна кількість тіків між min і max
        const totalTicks = Math.round((max - min) * TICKS_PER_UNIT) + 1;

        // індекс тіку для поточного zoom
        const currentTickIndex = Math.round((zoom - min) * TICKS_PER_UNIT);

        // зсуваємо всю лінійку так, щоб активний тік був по центру
        const offsetPx = -(currentTickIndex * TICK_SPACING);

        /**
         * Визначаємо розмір тіку:
         *  - великий: ціле число (1×, 2×, 3×)
         *  - середній: половинки (1.5×, 2.5×)
         *  - малий: все інше
         */
        const tickSize = (i: number): "large" | "medium" | "small" => {
            const v = min + i / TICKS_PER_UNIT;

            if (Math.abs(v - Math.round(v)) < 0.01) return "large";
            if (Math.abs((v * 2) % 1) < 0.01) return "medium";

            return "small";
        };

        return (
            <StyledRoot {...moveBind}>
                {/* ЛІНІЙКА */}
                <StyledSliderZone $visible={sliderMode}>
                    <StyledZoomBadge>{fmtZoom(zoom)}</StyledZoomBadge>

                    <StyledRulerWrap>
                        <StyledTicksRow
                            style={{
                                transform: `translateX(${offsetPx}px)`,
                            }}
                        >
                            {Array.from({ length: totalTicks }, (_, i) => (
                                <StyledTick key={i} $size={tickSize(i)} />
                            ))}
                        </StyledTicksRow>

                        {/* Нерухома центральна риска */}
                        <StyledCenterLine />
                    </StyledRulerWrap>
                </StyledSliderZone>

                {/* ПРЕСЕТИ (кнопки типу 1×, 2×, 3×) */}
                <StyledPresetsRow $hidden={sliderMode} {...triggerBind}>
                    {presets.map((z) => (
                        <StyledPresetBtn
                            key={z}
                            $active={Math.abs(zoom - z) < 0.06}
                            onClick={() => handlePreset(z)}
                        >
                            {z === 0.5 ? ".5" : `${z}×`}
                        </StyledPresetBtn>
                    ))}
                </StyledPresetsRow>
            </StyledRoot>
        );
    },
);

ZoomControls.displayName = "ZoomControls";
